import { useState } from 'react'
import {
  NO_FILTERS,
  STATE_FILTERS,
  type Filters,
} from '@core/filters'
import { parseAmountToCents } from '@core/money'
import type { PaymentState } from '@core/payments'
import { Button, Field } from '@design/components'
import { Overlay } from '@design/Overlay'

/**
 * The filter sheet.
 *
 * Written as the questions people actually ask — "wat staat er nog open bij
 * Metro", "wat is er boven de vijfhonderd" — rather than as fields on a query.
 * Everything is optional and everything narrows; there is no mode to get into
 * and nothing to reset except one button that says so.
 *
 * Applied on Toepassen, not on every keystroke: a list that reshuffles under
 * your thumb while you are still typing is unusable on a phone.
 */
export function FilterSheet({
  initial,
  onApply,
  onClose,
}: {
  initial: Filters
  onApply: (filters: Filters) => void
  onClose: () => void
}) {
  const [draft, setDraft] = useState<Filters>(initial)
  const [min, setMin] = useState(initial.minCents === null ? '' : (initial.minCents / 100).toFixed(2))
  const [max, setMax] = useState(initial.maxCents === null ? '' : (initial.maxCents / 100).toFixed(2))

  function toggleState(state: PaymentState) {
    setDraft((d) => ({
      ...d,
      states: d.states.includes(state)
        ? d.states.filter((s) => s !== state)
        : [...d.states, state],
    }))
  }

  function apply() {
    onApply({
      ...draft,
      minCents: min.trim() ? parseAmountToCents(min) : null,
      maxCents: max.trim() ? parseAmountToCents(max) : null,
    })
  }

  return (
    <Overlay>
      <header className="shrink-0 pad-safe-top px-3 pb-2 flex items-center justify-between border-b border-line">
        <button onClick={onClose} className="text-muted px-2 py-2">
          Annuleren
        </button>
        <span className="font-medium">Filteren</span>
        <button
          onClick={() => {
            setDraft(NO_FILTERS)
            setMin('')
            setMax('')
          }}
          className="text-muted px-2 py-2 text-sm"
        >
          Wissen
        </button>
      </header>

      <div className="flex-1 scroll-area px-4 py-4">
        <p className="text-sm text-muted mb-1.5">Status</p>
        <div className="flex flex-wrap gap-2 mb-5">
          {STATE_FILTERS.map(({ state, label }) => (
            <button
              key={state}
              onClick={() => toggleState(state)}
              className={`min-h-[2.75rem] px-4 rounded-xl border text-sm font-medium ${
                draft.states.includes(state)
                  ? 'border-ink bg-ink/5'
                  : 'border-line text-muted'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <Field
          label="Leverancier"
          value={draft.supplier}
          onChange={(e) => setDraft({ ...draft, supplier: e.target.value })}
          placeholder="Metro, Sibelga, huur…"
          autoCorrect="off"
          autoCapitalize="none"
        />

        <p className="text-sm text-muted mb-1.5">Bedrag</p>
        <div className="flex items-center gap-2 mb-5">
          <input
            value={min}
            onChange={(e) => setMin(e.target.value)}
            inputMode="decimal"
            placeholder="vanaf"
            className="flex-1 min-h-[3.25rem] px-4 rounded-xl bg-surface border border-line"
          />
          <span className="text-muted text-sm">tot</span>
          <input
            value={max}
            onChange={(e) => setMax(e.target.value)}
            inputMode="decimal"
            placeholder="tot"
            className="flex-1 min-h-[3.25rem] px-4 rounded-xl bg-surface border border-line"
          />
        </div>

        <label className="flex items-center justify-between gap-3 py-2">
          <span className="text-sm">
            Alleen wat niet kan wachten
            <span className="block text-xs text-muted">
              Overheid, fiscus, huur, verzekering, deurwaarder.
            </span>
          </span>
          <input
            type="checkbox"
            checked={draft.hardOnly}
            onChange={(e) => setDraft({ ...draft, hardOnly: e.target.checked })}
            className="w-6 h-6 shrink-0"
          />
        </label>
      </div>

      <div className="shrink-0 px-4 pb-4 pt-2 pad-safe-bottom border-t border-line">
        <Button size="large" onClick={apply}>
          Toepassen
        </Button>
      </div>
    </Overlay>
  )
}
